/**
 * Financial Position — single source of truth for high-level cash summaries.
 *
 * Consumers (must not recompute these elsewhere):
 * - Finance module dashboard
 * - Desktop KPI / Operations workspace
 * - Mobile dashboard carousel
 * - Future Ashley AI Read Model (`toFinancialPositionReadModel`)
 *
 * Phase 2B.5: Current, Reserved, Available, Money In/Out Today.
 * Expected Cash: sum of unpaid sent invoices (status=sent only).
 * Committed remains null until obligations land.
 * Future: aging / due-date weighting / per-deposit-account Expected Cash.
 */

import { centsToDecimal, outstandingCents } from "./invoiceMoney";
import { isMissingInvoicePaymentsRelation } from "./invoices";
import { listFinanceAccounts } from "./financeAccounts";

/**
 * Local calendar date as YYYY-MM-DD (owner "today", not UTC).
 */
export function localIsoDate(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Balance contribution for one movement against an account class.
 * Asset: inbound +, outbound −
 * Liability: outbound + (draw/charge), inbound − (payment)
 */
export function movementBalanceDelta(accountClass, flow, amount) {
  const value = Number(amount);
  const safe = Number.isFinite(value) ? value : 0;
  if (accountClass === "liability") {
    return flow === "outbound" ? safe : -safe;
  }
  return flow === "inbound" ? safe : -safe;
}

function resolveMovementEventType(movement) {
  if (!movement) {
    return null;
  }
  if (movement.event_type) {
    return movement.event_type;
  }
  if (movement.finance_events?.event_type) {
    return movement.finance_events.event_type;
  }
  return null;
}

function isPostedMovement(movement) {
  return !movement.status || movement.status === "posted";
}

/**
 * Compute balance for one account from opening + posted movements.
 */
export function computeAccountBalance(account, movements = []) {
  let balance = Number(account?.opening_balance);
  if (!Number.isFinite(balance)) {
    balance = 0;
  }

  const accountClass = account?.class || "asset";
  for (const movement of movements) {
    if (!isPostedMovement(movement)) {
      continue;
    }
    balance += movementBalanceDelta(
      accountClass,
      movement.flow,
      movement.amount,
    );
  }

  return balance;
}

/**
 * Expected Cash — total still receivable from customers.
 * Includes only unpaid sent invoices (draft / paid / archived contribute 0).
 * Reuses AR outstanding balance so invoice status rules stay in one place.
 *
 * @param {object[]} invoices
 * @returns {number}
 */
export function computeExpectedCash(invoices = []) {
  let cents = 0;
  for (const invoice of invoices || []) {
    cents += outstandingCents(invoice);
  }
  return Number(centsToDecimal(cents));
}

/**
 * Sales Tax Held — tax collected on paid invoices, less remittances.
 *
 * Remittances are optional today (empty). When tax remittance Money Out posts
 * land, pass them here so held decreases without changing invoice workflow.
 *
 * @param {object[]} paidInvoices — rows with status/total_tax (ideally status=paid)
 * @param {object[]} [remittances] — `{ amount }` or `{ total_amount }` tax remitted
 * @returns {number}
 */
export function computeSalesTaxHeld(paidInvoices = [], remittances = []) {
  let held = 0;
  for (const invoice of paidInvoices || []) {
    const status = String(invoice?.status || "").toLowerCase();
    if (status !== "paid") {
      continue;
    }
    const tax = Number(invoice.total_tax);
    if (Number.isFinite(tax) && tax > 0) {
      held += tax;
    }
  }

  for (const remittance of remittances || []) {
    const amount = Number(
      remittance?.amount ?? remittance?.total_amount ?? remittance?.tax_amount,
    );
    if (Number.isFinite(amount) && amount > 0) {
      held -= amount;
    }
  }

  return held;
}

/**
 * Sales Tax Held allocated to deposit accounts (paid invoices only).
 * Invoices without deposit_account_id are omitted from per-account maps
 * but still count toward the org total via computeSalesTaxHeld.
 *
 * @returns {Map<string, number>}
 */
export function computeSalesTaxHeldByAccount(
  paidInvoices = [],
  remittances = [],
) {
  const byAccount = new Map();

  for (const invoice of paidInvoices || []) {
    const status = String(invoice?.status || "").toLowerCase();
    if (status !== "paid") {
      continue;
    }
    const accountId = invoice.deposit_account_id || invoice.depositAccountId;
    if (!accountId) {
      continue;
    }
    const tax = Number(invoice.total_tax);
    if (!Number.isFinite(tax) || tax <= 0) {
      continue;
    }
    byAccount.set(accountId, (byAccount.get(accountId) || 0) + tax);
  }

  for (const remittance of remittances || []) {
    const accountId =
      remittance?.deposit_account_id || remittance?.depositAccountId || null;
    if (!accountId) {
      continue;
    }
    const amount = Number(
      remittance?.amount ?? remittance?.total_amount ?? remittance?.tax_amount,
    );
    if (!Number.isFinite(amount) || amount <= 0) {
      continue;
    }
    byAccount.set(accountId, (byAccount.get(accountId) || 0) - amount);
  }

  return byAccount;
}

/**
 * Spendable cash on one account (shared by account cards + Cash Position).
 * Tax-reserve balances are not spendable (covered by Reserved Cash).
 * Operating / undeposited subtract Sales Tax Held on that deposit account.
 */
export function computeAccountAvailableCash(account, salesTaxHeld = 0) {
  if (!account || account.active === false || account.class === "liability") {
    return 0;
  }

  const balance = Number(account.balance);
  const safeBalance = Number.isFinite(balance) ? balance : 0;
  const purpose = account.purpose || "other";
  const taxHeld = Math.max(0, Number(salesTaxHeld) || 0);

  if (purpose === "tax_reserve") {
    return 0;
  }

  if (purpose === "operating" || purpose === "undeposited") {
    return Math.max(0, safeBalance - taxHeld);
  }

  return Math.max(0, safeBalance);
}

/**
 * Org Available Cash — one formula for Cash Position header.
 * Available = Current − Reserved − Sales Tax Held (never negative).
 */
export function computeAvailableCash({
  currentCash = 0,
  reservedCash = 0,
  salesTaxHeld = 0,
} = {}) {
  const current = Number(currentCash);
  const reserved = Number(reservedCash);
  const taxHeld = Number(salesTaxHeld);
  return Math.max(
    0,
    (Number.isFinite(current) ? current : 0) -
      (Number.isFinite(reserved) ? reserved : 0) -
      Math.max(0, Number.isFinite(taxHeld) ? taxHeld : 0),
  );
}

/**
 * Data-driven metric rows for an account summary card.
 * Different purposes expose different composition fields.
 *
 * @returns {{ key: string, label: string, value: number }[]}
 */
export function buildFinanceAccountCardMetrics(
  account,
  { salesTaxHeld = 0 } = {},
) {
  const balance = Number(account?.balance);
  const safeBalance = Number.isFinite(balance) ? balance : 0;
  const purpose = account?.purpose || "other";
  const taxHeld = Number(salesTaxHeld);
  const safeTaxHeld = Number.isFinite(taxHeld) ? taxHeld : 0;

  const metrics = [
    { key: "balance", label: "Balance", value: safeBalance },
  ];

  if (purpose === "operating" || purpose === "undeposited") {
    metrics.push({
      key: "available",
      label: "Available",
      value: computeAccountAvailableCash(
        { ...account, balance: safeBalance, purpose, class: account?.class },
        safeTaxHeld,
      ),
    });
    metrics.push({
      key: "sales_tax_held",
      label: "Sales Tax Held",
      value: Math.max(0, safeTaxHeld),
    });
    return metrics;
  }

  if (purpose === "tax_reserve") {
    metrics.push({
      key: "gst_hst_held",
      label: "GST/HST Held",
      value: safeBalance,
    });
    return metrics;
  }

  if (purpose === "payroll_float") {
    metrics.push({
      key: "payroll_reserve",
      label: "Payroll Reserve",
      value: safeBalance,
    });
    return metrics;
  }

  // savings / other / liabilities — balance only for now
  return metrics;
}

/**
 * Derive org Financial Position from accounts + movements.
 *
 * Money In/Out Today: posted asset-account flows on asOfDate, excluding
 * internal transfers (those are not business money in/out).
 *
 * Sales Tax Held: tax on paid invoices minus remittances (commercial tax
 * liability sitting in cash until remitted).
 * Available Cash: Current − Reserved − Sales Tax Held (same model as account
 * card Available via computeAvailableCash / computeAccountAvailableCash).
 *
 * @param {object[]} accounts
 * @param {object[]} movements
 * @param {{
 *   asOfDate?: string,
 *   expectedCash?: number|null,
 *   invoices?: object[],
 *   paidTaxInvoices?: object[],
 *   taxRemittances?: object[],
 * }} [options]
 */
export function computeFinancePosition(accounts = [], movements = [], options = {}) {
  const asOfDate = options.asOfDate || localIsoDate();
  const byAccount = new Map();
  for (const movement of movements) {
    if (!movement?.account_id) {
      continue;
    }
    if (!isPostedMovement(movement)) {
      continue;
    }
    const list = byAccount.get(movement.account_id) || [];
    list.push(movement);
    byAccount.set(movement.account_id, list);
  }

  const accountById = new Map(
    (accounts || []).filter(Boolean).map((account) => [account.id, account]),
  );

  const paidTaxInvoices = options.paidTaxInvoices || [];
  const taxRemittances = options.taxRemittances || [];
  const salesTaxHeld = computeSalesTaxHeld(paidTaxInvoices, taxRemittances);
  const salesTaxHeldByAccount = computeSalesTaxHeldByAccount(
    paidTaxInvoices,
    taxRemittances,
  );

  const accountRows = [];
  let currentCash = 0;
  let reservedCash = 0;
  let moneyInToday = 0;
  let moneyOutToday = 0;

  for (const account of accounts) {
    if (!account || account.active === false) {
      continue;
    }

    const balance = computeAccountBalance(
      account,
      byAccount.get(account.id) || [],
    );
    const accountSalesTaxHeld = salesTaxHeldByAccount.get(account.id) || 0;

    accountRows.push({
      accountId: account.id,
      name: account.name,
      purpose: account.purpose,
      instrument: account.instrument,
      class: account.class,
      currency: account.currency || "CAD",
      balance,
      salesTaxHeld: accountSalesTaxHeld,
      availableCash: computeAccountAvailableCash(
        {
          balance,
          purpose: account.purpose,
          class: account.class,
          active: true,
        },
        accountSalesTaxHeld,
      ),
      metrics: buildFinanceAccountCardMetrics(
        {
          balance,
          purpose: account.purpose,
          class: account.class,
        },
        { salesTaxHeld: accountSalesTaxHeld },
      ),
    });

    if (account.class === "asset") {
      currentCash += balance;
      if (account.purpose === "tax_reserve") {
        reservedCash += balance;
      }
    }
  }

  for (const movement of movements) {
    if (!isPostedMovement(movement)) {
      continue;
    }
    if (movement.occurred_on !== asOfDate) {
      continue;
    }
    if (resolveMovementEventType(movement) === "transfer") {
      continue;
    }

    const account = accountById.get(movement.account_id);
    if (!account || account.active === false || account.class !== "asset") {
      continue;
    }

    const amount = Number(movement.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      continue;
    }

    if (movement.flow === "inbound") {
      moneyInToday += amount;
    } else if (movement.flow === "outbound") {
      moneyOutToday += amount;
    }
  }

  const availableCash = computeAvailableCash({
    currentCash,
    reservedCash,
    salesTaxHeld,
  });
  const currency =
    accountRows.find((row) => row.class === "asset")?.currency || "CAD";

  let expectedCash = null;
  if (options.expectedCash !== undefined && options.expectedCash !== null) {
    const value = Number(options.expectedCash);
    expectedCash = Number.isFinite(value) ? value : 0;
  } else if (options.invoices !== undefined) {
    expectedCash = computeExpectedCash(options.invoices);
  }

  return {
    asOfDate,
    currency,
    currentCash,
    reservedCash,
    availableCash,
    salesTaxHeld,
    moneyInToday,
    moneyOutToday,
    expectedCash,
    committedCash: null,
    accounts: accountRows,
  };
}

/** Display groups for where-is-my-money summaries (owner language). */
export const FINANCE_ACCOUNT_SUMMARY_GROUPS = Object.freeze([
  { id: "operating", label: "Operating" },
  { id: "savings", label: "Savings" },
  { id: "tax_reserve", label: "Tax Reserve" },
  { id: "credit_card", label: "Credit Cards" },
  { id: "loan", label: "Loans" },
  { id: "other_asset", label: "Other Cash" },
  { id: "other_liability", label: "Other Liabilities" },
]);

/**
 * Map a position account row to a summary group id.
 */
export function financeAccountSummaryGroupId(account) {
  if (!account) {
    return "other_asset";
  }
  if (account.purpose === "operating") {
    return "operating";
  }
  if (account.purpose === "savings") {
    return "savings";
  }
  if (account.purpose === "tax_reserve") {
    return "tax_reserve";
  }
  if (account.instrument === "credit_card") {
    return "credit_card";
  }
  if (account.instrument === "loan") {
    return "loan";
  }
  if (account.class === "liability") {
    return "other_liability";
  }
  return "other_asset";
}

/**
 * Group active account balances for Finance landing / dashboard widgets.
 * Totals: cashTotal (assets), liabilityTotal (credit/loans), overall from groups.
 */
export function groupFinanceAccountsForSummary(accounts = []) {
  const buckets = new Map(
    FINANCE_ACCOUNT_SUMMARY_GROUPS.map((group) => [
      group.id,
      { ...group, accounts: [], total: 0 },
    ]),
  );

  let cashTotal = 0;
  let liabilityTotal = 0;

  for (const account of accounts || []) {
    if (!account) {
      continue;
    }
    const groupId = financeAccountSummaryGroupId(account);
    const bucket = buckets.get(groupId) || buckets.get("other_asset");
    const balance = Number(account.balance);
    const safe = Number.isFinite(balance) ? balance : 0;
    bucket.accounts.push(account);
    bucket.total += safe;

    if (account.class === "liability") {
      liabilityTotal += safe;
    } else {
      cashTotal += safe;
    }
  }

  const groups = FINANCE_ACCOUNT_SUMMARY_GROUPS.map((group) =>
    buckets.get(group.id),
  ).filter((group) => group.accounts.length > 0);

  return {
    groups,
    cashTotal,
    liabilityTotal,
    accountCount: (accounts || []).length,
  };
}

/**
 * Ashley-ready Read Model. No Finance table access — pass a position snapshot.
 * Voice can answer cash / available / reserved / today in-out from this shape.
 */
export function toFinancialPositionReadModel(position = {}) {
  return {
    schema: "FinanceReadModelV1",
    asOfDate: position.asOfDate || localIsoDate(),
    currency: position.currency || "CAD",
    currentCash: Number(position.currentCash) || 0,
    availableCash: Number(position.availableCash) || 0,
    reservedCash: Number(position.reservedCash) || 0,
    salesTaxHeld: Number(position.salesTaxHeld) || 0,
    moneyInToday: Number(position.moneyInToday) || 0,
    moneyOutToday: Number(position.moneyOutToday) || 0,
    expectedCash:
      position.expectedCash === undefined || position.expectedCash === null
        ? null
        : Number(position.expectedCash),
    committedCash:
      position.committedCash === undefined || position.committedCash === null
        ? null
        : Number(position.committedCash),
    accounts: Array.isArray(position.accounts)
      ? position.accounts.map((account) => ({
          accountId: account.accountId,
          name: account.name,
          group: financeAccountSummaryGroupId(account),
          balance: Number(account.balance) || 0,
          salesTaxHeld: Number(account.salesTaxHeld) || 0,
          currency: account.currency || "CAD",
        }))
      : [],
  };
}

/**
 * Load posted movements for position math (org-scoped via RLS).
 * Includes parent event_type so today totals can exclude transfers.
 */
export async function loadPostedMovements(client) {
  if (!client) {
    throw new Error("Supabase client is required.");
  }

  const { data, error } = await client
    .from("finance_movements")
    .select(
      "id, organization_id, event_id, account_id, flow, amount, currency, occurred_on, status, finance_events ( event_type )",
    )
    .eq("status", "posted");

  if (error) {
    throw error;
  }
  return data || [];
}

/**
 * Load unpaid sent invoices used for Expected Cash (org-scoped via RLS).
 * Status filter only — draft / paid / archived never appear.
 * PK is invoice_id (not id).
 */
export async function loadExpectedCashInvoices(client) {
  if (!client) {
    throw new Error("Supabase client is required.");
  }

  let { data, error } = await client
    .from("invoices")
    .select(
      "invoice_id, status, total_amount, invoice_payments(payment_id, amount, payment_role, reversal_of_payment_id)",
    )
    .eq("status", "sent");

  if (isMissingInvoicePaymentsRelation(error)) {
    ({ data, error } = await client
      .from("invoices")
      .select("invoice_id, status, total_amount")
      .eq("status", "sent"));
  }

  if (error) {
    throw error;
  }
  return data || [];
}

/**
 * Load paid invoices for Sales Tax Held (org-scoped via RLS).
 * Tax liability is commercial: total_tax collected when invoices are paid.
 */
export async function loadPaidTaxInvoices(client) {
  if (!client) {
    throw new Error("Supabase client is required.");
  }

  const { data, error } = await client
    .from("invoices")
    .select("invoice_id, status, total_tax, deposit_account_id, paid_at")
    .eq("status", "paid");

  if (error) {
    throw error;
  }
  return data || [];
}

/**
 * Load full Financial Position snapshot for the signed-in Finance org.
 * Prefer this (or toFinancialPositionReadModel of its result) everywhere.
 *
 * Empty accounts / movements / invoices yield $0 totals — not an error.
 * Expected Cash / Sales Tax Held loads are isolated so invoice-query failures
 * cannot blank Cash Position.
 */
export async function loadFinancePosition(client, options = {}) {
  const [accounts, movements] = await Promise.all([
    listFinanceAccounts(client, { includeInactive: true }),
    loadPostedMovements(client),
  ]);

  let invoices = [];
  try {
    invoices = await loadExpectedCashInvoices(client);
  } catch (err) {
    console.error("[Finance] expected cash invoices load failed:", err);
    invoices = [];
  }

  let paidTaxInvoices = [];
  try {
    paidTaxInvoices = await loadPaidTaxInvoices(client);
  } catch (err) {
    console.error("[Finance] sales tax held invoices load failed:", err);
    paidTaxInvoices = [];
  }

  return computeFinancePosition(accounts, movements, {
    ...options,
    invoices,
    paidTaxInvoices,
    taxRemittances: options.taxRemittances || [],
  });
}

/** Alias — product language for dashboards / Ashley. */
export const loadFinancialPosition = loadFinancePosition;
